import {Component, ElementRef, HostListener, Input, ViewChild} from '@angular/core';
import {NgClass, NgIf} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

@Component({
  selector: 'app-info-tooltip',
  standalone: true,
  imports: [
    NgClass,
    TranslocoPipe,
  ],
  templateUrl: './info-tooltip.component.html',
  styleUrl: './info-tooltip.component.scss'
})
export class InfoTooltipComponent {
  private static nextId = 0;
  private static readonly VIEWPORT_PADDING = 12;
  private repositionFrame: number | null = null;

  @Input({required: true}) public textKey!: string;
  @Input() public params?: Record<string, unknown>;
  @Input() public ariaLabelKey?: string;
  @Input() public placement: TooltipPlacement = 'top';

  @ViewChild('bubbleElement') private bubbleElement?: ElementRef<HTMLDivElement>;

  public isVisible = false;
  public currentPlacement: TooltipPlacement = 'top';
  public horizontalShift = 0;
  public verticalShift = 0;
  public readonly tooltipId = `info-tooltip-${InfoTooltipComponent.nextId++}`;

  public show(): void {
    if (this.isVisible) {
      return;
    }
    this.isVisible = true;
    this.currentPlacement = this.placement;
    this.resetOffsets();
    this.scheduleReposition();
  }

  public hide(): void {
    this.isVisible = false;
    this.cancelScheduledReposition();
  }

  public toggle(event: MouseEvent): void {
    event.preventDefault();
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  public handleViewportChange(): void {
    this.scheduleReposition();
  }

  private scheduleReposition(): void {
    if (!this.isVisible || typeof window === 'undefined') {
      return;
    }
    this.cancelScheduledReposition();
    this.repositionFrame = window.requestAnimationFrame(() => {
      this.repositionFrame = null;
      this.constrainToViewport();
    });
  }

  private cancelScheduledReposition(): void {
    if (this.repositionFrame != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.repositionFrame);
      this.repositionFrame = null;
    }
  }

  private constrainToViewport(): void {
    if (!this.isVisible || typeof window === 'undefined') {
      return;
    }

    const bubble = this.bubbleElement?.nativeElement;
    if (!bubble) {
      // The view might not be ready yet, try again on the next frame.
      this.scheduleReposition();
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bubbleRect = bubble.getBoundingClientRect();

    const overflowTop = bubbleRect.top < InfoTooltipComponent.VIEWPORT_PADDING;
    const overflowBottom = bubbleRect.bottom > viewportHeight - InfoTooltipComponent.VIEWPORT_PADDING;
    const overflowLeft = bubbleRect.left < InfoTooltipComponent.VIEWPORT_PADDING;
    const overflowRight = bubbleRect.right > viewportWidth - InfoTooltipComponent.VIEWPORT_PADDING;

    if (this.currentPlacement === 'top' && overflowTop && !overflowBottom) {
      this.currentPlacement = 'bottom';
      this.resetOffsets();
      this.scheduleReposition();
      return;
    }

    if (this.currentPlacement === 'bottom' && overflowBottom && !overflowTop) {
      this.currentPlacement = 'top';
      this.resetOffsets();
      this.scheduleReposition();
      return;
    }

    if (this.currentPlacement === 'left' && overflowLeft && !overflowRight) {
      this.currentPlacement = 'right';
      this.resetOffsets();
      this.scheduleReposition();
      return;
    }

    if (this.currentPlacement === 'right' && overflowRight && !overflowLeft) {
      this.currentPlacement = 'left';
      this.resetOffsets();
      this.scheduleReposition();
      return;
    }

    let horizontalShift = 0;
    let verticalShift = 0;

    if (this.currentPlacement === 'top' || this.currentPlacement === 'bottom') {
      if (overflowLeft) {
        horizontalShift = InfoTooltipComponent.VIEWPORT_PADDING - bubbleRect.left;
      } else if (overflowRight) {
        horizontalShift = (viewportWidth - InfoTooltipComponent.VIEWPORT_PADDING) - bubbleRect.right;
      }
    } else {
      if (overflowTop) {
        verticalShift = InfoTooltipComponent.VIEWPORT_PADDING - bubbleRect.top;
      } else if (overflowBottom) {
        verticalShift = (viewportHeight - InfoTooltipComponent.VIEWPORT_PADDING) - bubbleRect.bottom;
      }
    }

    this.horizontalShift = horizontalShift;
    this.verticalShift = verticalShift;
  }

  private resetOffsets(): void {
    this.horizontalShift = 0;
    this.verticalShift = 0;
  }
}
